import { useState } from "react";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { z } from "zod";
import { useLeadGuard } from "@/components/LeadGuard";
import { SITE } from "@/content/site";

type Lang = "en" | "bn";

/** Per-language copy. Field rules are shared; only wording differs. */
const COPY = {
  en: {
    title: "Want results like this?",
    description:
      "Tell me what you're working on. I reply within one business day with an honest take — and a quote if I can help.",
    eyebrow: "Quick brief",
    name: "Your name",
    email: "Email address",
    site: "Website (optional)",
    goal: "What are you trying to fix or grow?",
    send: "Send brief",
    sending: "Sending",
    fullForm: "Prefer the full contact form →",
    fullFormHref: "/contact",
    okTitle: "Got it — talk soon.",
    okBody: (email: string) => (
      <>
        I'll reply from <span className="text-foreground">{email}</span> within one business day.
      </>
    ),
    errName: "Please add your name.",
    errEmail: "Please enter a valid email address.",
    errGoal: "Please tell me briefly what you need help with.",
    errRate: "Too many submissions from this connection. Please wait a few minutes, or use the email link below.",
    errSend: "Couldn't send that from the browser — use the email fallback below.",
    mailFallback: "Send it as an email instead",
    mailSubject: "New project inquiry",
  },
  bn: {
    title: "আপনার প্রজেক্টের কথা জানান",
    description:
      "আপনি কী নিয়ে কাজ করছেন লিখুন। এক কর্মদিবসের মধ্যে সৎ মতামত জানাব — সাহায্য করতে পারলে কোটেশনসহ।",
    eyebrow: "সংক্ষিপ্ত ব্রিফ",
    name: "আপনার নাম",
    email: "ইমেইল ঠিকানা",
    site: "ওয়েবসাইট (ঐচ্ছিক)",
    goal: "কী ঠিক করতে বা বাড়াতে চান?",
    send: "ব্রিফ পাঠান",
    sending: "পাঠানো হচ্ছে",
    fullForm: "যোগাযোগ পেজে যান →",
    fullFormHref: "/bn/contact",
    okTitle: "পেয়েছি — শিগগিরই কথা হবে।",
    okBody: (email: string) => (
      <>
        এক কর্মদিবসের মধ্যে <span className="text-foreground">{email}</span> থেকে উত্তর দেব।
      </>
    ),
    errName: "অনুগ্রহ করে আপনার নাম লিখুন।",
    errEmail: "সঠিক ইমেইল ঠিকানা লিখুন।",
    errGoal: "কী বিষয়ে সাহায্য চান, সংক্ষেপে লিখুন।",
    errRate: "এই সংযোগ থেকে অনেকবার পাঠানো হয়েছে। কয়েক মিনিট পর চেষ্টা করুন, বা নিচের ইমেইল লিংক ব্যবহার করুন।",
    errSend: "ব্রাউজার থেকে পাঠানো যায়নি — নিচের ইমেইল লিংক ব্যবহার করুন।",
    mailFallback: "ইমেইলে পাঠান",
    mailSubject: "নতুন প্রজেক্ট অনুসন্ধান",
  },
} as const;

function buildSchema(c: (typeof COPY)[Lang]) {
  return z.object({
    name: z.string().trim().min(1, c.errName).max(100),
    email: z.string().trim().email(c.errEmail).max(255),
    site: z.string().trim().max(255).optional().or(z.literal("")),
    goal: z.string().trim().min(1, c.errGoal).max(1000),
  });
}

type Props = {
  /** Title above the form. */
  title?: string;
  /** Supporting blurb. */
  description?: string;
  /** Source tag passed to the newsletter endpoint for attribution. */
  source?: string;
  /** Interface language. */
  lang?: Lang;
  /** Show the "full contact form" link (hide it on the contact page itself). */
  showContactLink?: boolean;
};

const INPUT =
  "w-full min-w-0 rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-primary/60";

/**
 * Compact inline lead form for high-intent pages (case studies, services, booking).
 * Posts to /api/public/newsletter as lead capture and falls back to a pre-filled
 * mailto: link if the request fails, so a lead is never lost.
 */
export function InlineLeadForm({
  title,
  description,
  source = "inline-lead",
  lang = "en",
  showContactLink = true,
}: Props) {
  const c = COPY[lang];
  const [form, setForm] = useState({ name: "", email: "", site: "", goal: "" });
  const [state, setState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [errMsg, setErrMsg] = useState("");
  const [mailtoHref, setMailtoHref] = useState("");
  const { guardFields, honeypot } = useLeadGuard();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "loading") return;
    const parsed = buildSchema(c).safeParse(form);
    if (!parsed.success) {
      setState("err");
      setMailtoHref("");
      setErrMsg(parsed.error.issues[0]?.message ?? c.errSend);
      return;
    }
    setState("loading");
    const body = [
      `Name: ${parsed.data.name}`,
      `Email: ${parsed.data.email}`,
      parsed.data.site ? `Website: ${parsed.data.site}` : "",
      "",
      parsed.data.goal,
    ]
      .filter(Boolean)
      .join("\n");
    const fallback = `mailto:${SITE.email}?subject=${encodeURIComponent(c.mailSubject)}&body=${encodeURIComponent(body)}`;
    try {
      const res = await fetch("/api/public/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: parsed.data.email,
          source,
          ...guardFields(),
          meta: {
            name: parsed.data.name,
            site: parsed.data.site || undefined,
            goal: parsed.data.goal,
            lang,
          },
        }),
      });
      if (res.status === 429) {
        setMailtoHref(fallback);
        setState("err");
        setErrMsg(c.errRate);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setState("ok");
    } catch {
      // Never lose a lead: hand the visitor a pre-filled email instead.
      setMailtoHref(fallback);
      setState("err");
      setErrMsg(c.errSend);
    }
  };

  if (state === "ok") {
    return (
      <div role="status" className="rounded-3xl border border-primary/30 bg-primary/5 p-10 lg:p-14 text-center">
        <CheckCircle2 className="mx-auto text-primary" size={28} aria-hidden="true" />
        <h3 className="mt-4 font-display text-3xl">{c.okTitle}</h3>
        <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">{c.okBody(SITE.email)}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      lang={lang === "bn" ? "bn" : undefined}
      className="relative rounded-3xl border border-border bg-card p-6 sm:p-8 lg:p-12"
    >
      {honeypot}
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">{c.eyebrow}</div>
          <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">{title ?? c.title}</h3>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{description ?? c.description}</p>
        </div>
        <div className="lg:col-span-3 grid gap-4 min-w-0">
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={c.name}
              aria-label={c.name}
              autoComplete="name"
              maxLength={100}
              className={INPUT}
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@company.com"
              aria-label={c.email}
              autoComplete="email"
              maxLength={255}
              className={INPUT}
            />
          </div>
          <input
            value={form.site}
            onChange={(e) => setForm({ ...form, site: e.target.value })}
            placeholder={c.site}
            aria-label={c.site}
            autoComplete="url"
            maxLength={255}
            className={INPUT}
          />
          <textarea
            required
            rows={3}
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder={c.goal}
            aria-label={c.goal}
            maxLength={1000}
            className={`${INPUT} resize-none`}
          />
          {state === "err" && (
            <p role="alert" className="text-xs text-destructive">
              {errMsg}{" "}
              {mailtoHref && (
                <a href={mailtoHref} className="underline underline-offset-4">
                  {c.mailFallback}
                </a>
              )}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={state === "loading"}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:shadow-[var(--shadow-glow)] disabled:opacity-60"
            >
              {state === "loading" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {c.sending}
                </>
              ) : (
                <>
                  {c.send} <ArrowUpRight size={14} aria-hidden="true" />
                </>
              )}
            </button>
            {showContactLink && (
              <a href={c.fullFormHref} className="text-xs text-muted-foreground hover:text-foreground">
                {c.fullForm}
              </a>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
